import React, { useMemo } from 'react';
import {
  watermarkFrameCss,
  watermarkSvg,
  type WatermarkLockup as Lockup,
  type WatermarkTone,
} from 'fym-shared/src/music24/story/watermark';

import { TypedMemo } from '../../../../common/types';

/**
 * The Free watermark lockup in a Studio preview (design `asset-redesign/w0 Free watermark`): the same
 * art (`watermarkSvg` of fym-shared) at the same box as the file, so what the artist sees is the file.
 * The frame is drawn at file pixels and scaled by its parent. With `x` and `y` the lockup is placed
 * absolutely; without them it is a block in the flow.
 */
export const WatermarkLockup = TypedMemo(
  ({
    lockup,
    tone,
    x,
    y,
    glass = false,
    testId,
  }: {
    lockup: Lockup;
    tone: WatermarkTone;
    x?: number;
    y?: number;
    glass?: boolean;
    testId?: string;
  }) => {
    const html = useMemo(() => ({ __html: watermarkSvg(lockup, tone) }), [lockup, tone]);
    const placed = x !== undefined && y !== undefined;
    return (
      <div
        data-testid={testId}
        data-watermark-form={lockup.form}
        // The lockup sits at file pixels in a scaled frame, as every item of the Studio frames.
        style={{
          ...(placed ? { position: 'absolute', left: x, top: y } : { position: 'relative' }),
          ...watermarkFrameCss(lockup, glass),
          flex: 'none',
          lineHeight: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
        // The SVG is our own art from fym-shared: paths and fixed colours, no user text.
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={html}
      />
    );
  }
);
